import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

/**
 * FIG Letterhead PDF generator — outbound broker/client correspondence
 * (renewal submissions, cover letters, proposals).
 *
 * Same brand stationery as the invoice (src/utils/invoicePdf.js): logo top-left,
 * gold (#B8860B) rule under the header, ghosted leaf watermark, FROM/TO columns,
 * assembled footer. The invoice-specific line-item table is replaced by flowing
 * letter body with word-wrap and automatic page breaks.
 *
 * Brand assets + settings are fetched with the SAME loaders the invoice uses
 * (getBrandAssets / getAgencySettings). They are imported lazily so this module
 * can also be exercised in a bare Node/Jest context by passing `settings` and
 * `brandAssets` directly (no SharePoint token needed).
 *
 * IMPORTANT: this file does not touch invoicePdf.js or the invoice flow.
 */

// ---- Shared layout constants (identical to invoicePdf.js so a letterhead page
//      and an invoice page read as the same stationery) ----
const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const TOP_MARGIN = 50;
const BOTTOM_MARGIN = 40;
const LEFT_MARGIN = 50;
const RIGHT_MARGIN = PAGE_WIDTH - 50;
const CONTENT_WIDTH = RIGHT_MARGIN - LEFT_MARGIN;

// Static layout colors (not brand-dependent) — same values as the invoice
const GREY = rgb(0.45, 0.45, 0.45);
const LIGHT_LINE = rgb(0.85, 0.85, 0.85);

// Fallback brand colors (used if brandAssets.colors missing) — same as invoice
const FALLBACK_ACCENT = { r: 0.72, g: 0.53, b: 0.04 }; // #B8860B (gold rule)
const FALLBACK_TEXT = { r: 0.1, g: 0.1, b: 0.1 };       // #1A1A1A

// Body typography
const BODY_SIZE = 10;
const BODY_LEADING = 15;
const PARA_GAP = 9;
// Vertical space kept clear for the footer on each page. The footer rule sits at
// BOTTOM_MARGIN + 22 (= 62); this floor (BOTTOM_MARGIN + 38 = 78) leaves ~16pt of
// clearance above it so body lines can fill the page instead of breaking early.
const FOOTER_RESERVE = 38;

/**
 * Fallback agency/payment values — mirror the seed defaults in
 * src/utils/invoiceSettings.js. ONLY used when neither `settings` nor `getToken`
 * is supplied (e.g. dev preview). In the real app, live FIG_Settings values are
 * fetched via getToken and take precedence.
 */
const DEFAULT_SETTINGS = {
  AgencyLegalName: 'Fragos Insurance Group, LLC',
  AgencyAddress: '16915 Walden Rd Suite 312',
  AgencyCity: 'Montgomery',
  AgencyState: 'TX',
  AgencyZip: '77356',
  AgencyPhone: '(936) 755-2521',
  AgencyWebsite: 'myfiginsurance.com',
  PaymentBankName: 'JPMorgan Chase Bank, NA',
  PaymentRouting: '111000614',
  PaymentAccount: '672110957',
  PaymentPayableTo: 'Fragos Insurance Group, LLC',
};

/**
 * Generate a FIG letterhead PDF.
 *
 * @param {Object}   opts
 * @param {Object}   opts.recipientBlock   { name, org, addressLines: [] } — the TO: block
 * @param {string}   [opts.dateStr]        e.g. "July 9, 2026"; default = today
 * @param {string}   [opts.subjectLine]    optional bold "RE: ..." line
 * @param {Array}    [opts.bodyBlocks]     array of paragraph strings OR { heading, text }
 * @param {Object}   [opts.signatureBlock] { name, title, agencyLine }; default = Marc Fragos / FIG
 * @param {boolean}  [opts.includeAch]     render ACH/Wire block; default false
 * @param {string}   [opts.footerOverride] override the assembled footer string
 * @param {Function} [opts.getToken]       async token accessor — when provided, settings +
 *                                         brandAssets are fetched via the invoice loaders
 * @param {Object}   [opts.settings]       pre-fetched agency settings (skips getToken fetch)
 * @param {Object}   [opts.brandAssets]    pre-fetched { logoBytes, leafBytes, colors }
 * @returns {Promise<Uint8Array>} PDF bytes
 */
export async function generateLetterheadPDF(opts = {}) {
  const {
    recipientBlock = {},
    dateStr,
    subjectLine,
    bodyBlocks = [],
    signatureBlock,
    includeAch = false,
    includeFrom = true,
    includeDate = true,
    footerOverride,
    getToken,
  } = opts;

  let { settings, brandAssets } = opts;

  // Reuse the invoice's settings + brand-asset loaders (imported lazily so this
  // module loads with pdf-lib alone when settings/brandAssets are passed in).
  if ((!settings || !brandAssets) && getToken) {
    const [{ getAgencySettings }, { getBrandAssets }] = await Promise.all([
      import('./invoiceSettings'),
      import('./brandAssets'),
    ]);
    if (!settings) settings = await getAgencySettings(getToken);
    if (!brandAssets) brandAssets = await getBrandAssets(getToken);
  }

  settings = { ...DEFAULT_SETTINGS, ...(settings || {}) };
  brandAssets = brandAssets || {};

  const pdfDoc = await PDFDocument.create();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const timesBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);

  // Brand colors — gold is the only brand color actually stroked (matches invoice)
  const bc = brandAssets.colors || {};
  const ACCENT = toRgb(bc.accent, FALLBACK_ACCENT);
  const DARK = toRgb(bc.text, FALLBACK_TEXT);

  // Embed logo + leaf if available (same embed/fallback logic as the invoice)
  let logoImage = null;
  if (brandAssets.logoBytes) {
    try { logoImage = await pdfDoc.embedPng(brandAssets.logoBytes); }
    catch { try { logoImage = await pdfDoc.embedJpg(brandAssets.logoBytes); } catch { /* text fallback */ } }
  }
  let leafImage = null;
  if (brandAssets.leafBytes) {
    try { leafImage = await pdfDoc.embedPng(brandAssets.leafBytes); }
    catch { try { leafImage = await pdfDoc.embedJpg(brandAssets.leafBytes); } catch { /* skip */ } }
  }

  const s = (v) => v || '';
  const agencyName = s(settings.AgencyLegalName);
  const dateText = dateStr || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const footerLine = footerOverride || buildFooter(settings, s);

  const pages = [];
  let currentPage;
  let y;

  // ---- Watermark leaf — same treatment as invoicePdf.js drawWatermarkLeaf() ----
  function drawWatermark(page) {
    if (!leafImage) return;
    const dims = leafImage.scale(1);
    const wmW = 600;
    const wmH = (dims.height / dims.width) * wmW;
    const wmX = PAGE_WIDTH - 400;          // bleeds off the right edge
    const wmY = (PAGE_HEIGHT - wmH) / 2;
    page.drawImage(leafImage, { x: wmX, y: wmY, width: wmW, height: wmH, opacity: 0.06 });
  }

  // ---- Full header (page 1): logo top-left, optional date top-right, gold rule ----
  function drawFullHeader(page) {
    let py = PAGE_HEIGHT - TOP_MARGIN;

    if (logoImage) {
      const dims = logoImage.scale(1);
      const logoH = 60;
      const logoW = (dims.width / dims.height) * logoH;
      page.drawImage(logoImage, { x: LEFT_MARGIN, y: py - logoH + 16, width: logoW, height: logoH });
    } else {
      page.drawText('F.I.G.', { x: LEFT_MARGIN, y: py, font: timesBold, size: 28, color: DARK });
    }

    // Right: date only (nothing else, unlike the invoice's INVOICE #/number)
    if (includeDate) {
      const dateW = helvetica.widthOfTextAtSize(dateText, 10);
      page.drawText(dateText, { x: RIGHT_MARGIN - dateW, y: py, font: helvetica, size: 10, color: GREY });
    }

    py -= 50;
    page.drawLine({ start: { x: LEFT_MARGIN, y: py }, end: { x: RIGHT_MARGIN, y: py }, thickness: 1.5, color: ACCENT });
    return py;
  }

  // ---- Compact header (continuation pages) — mirrors invoice drawContinuationHeader ----
  function drawCompactHeader(page) {
    let py = PAGE_HEIGHT - TOP_MARGIN;
    page.drawText('F.I.G.', { x: LEFT_MARGIN, y: py, font: timesBold, size: 14, color: DARK });
    page.drawText(agencyName.toUpperCase(), { x: LEFT_MARGIN, y: py - 14, font: helveticaBold, size: 8, color: GREY });
    py -= 38;
    page.drawLine({ start: { x: LEFT_MARGIN, y: py }, end: { x: RIGHT_MARGIN, y: py }, thickness: 1, color: ACCENT });
    return py - 10;
  }

  // ---- Footer — gold rule + assembled footer string, on every page ----
  function drawFooter(page) {
    const ruleY = BOTTOM_MARGIN + 22;
    page.drawLine({ start: { x: LEFT_MARGIN, y: ruleY }, end: { x: RIGHT_MARGIN, y: ruleY }, thickness: 1, color: ACCENT });
    const w = helvetica.widthOfTextAtSize(footerLine, 8);
    page.drawText(footerLine, { x: (PAGE_WIDTH - w) / 2, y: BOTTOM_MARGIN + 8, font: helvetica, size: 8, color: GREY });
  }

  function newPage(full) {
    const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    pages.push(page);
    drawWatermark(page);
    currentPage = page;
    y = full ? drawFullHeader(page) : drawCompactHeader(page);
  }

  // Ensure `needed` points of vertical space remain above the footer zone,
  // else start a continuation page.
  function ensureSpace(needed) {
    if (y - needed < BOTTOM_MARGIN + FOOTER_RESERVE) newPage(false);
  }

  function drawParagraph(text, font, size, color, indent = 0) {
    const lines = wrapText(font, text, size, CONTENT_WIDTH - indent);
    for (const line of lines) {
      ensureSpace(BODY_LEADING);
      y -= BODY_LEADING;
      currentPage.drawText(line, { x: LEFT_MARGIN + indent, y, font, size, color });
    }
  }

  // ========== PAGE 1 ==========
  newPage(true);

  // FROM (agency) + TO (recipient) as two columns — same positions as invoice FROM/BILL TO.
  // Both are independently skippable: FROM via includeFrom, TO when there's no recipient.
  y -= 20;
  const columnTopY = y; // shared baseline row for the FROM: / TO: labels

  const fromLines = [
    agencyName,
    s(settings.AgencyAddress),
    [s(settings.AgencyCity), s(settings.AgencyState), s(settings.AgencyZip)].filter(Boolean).join(', '),
  ].filter(Boolean);
  const showFrom = includeFrom && fromLines.length > 0;
  if (showFrom) {
    currentPage.drawText('FROM:', { x: LEFT_MARGIN, y: columnTopY, font: helveticaBold, size: 8, color: GREY });
    let fy = columnTopY - 14;
    for (const line of fromLines) {
      currentPage.drawText(line, { x: LEFT_MARGIN, y: fy, font: helvetica, size: 9, color: DARK });
      fy -= 13;
    }
  }

  const toX = PAGE_WIDTH / 2 + 20;
  const toLines = [
    s(recipientBlock.name),
    s(recipientBlock.org),
    ...((recipientBlock.addressLines || []).map(s)),
  ].filter(Boolean);
  if (toLines.length) {
    let toY = columnTopY;
    currentPage.drawText('TO:', { x: toX, y: toY, font: helveticaBold, size: 8, color: GREY });
    toY -= 14;
    for (const line of toLines) {
      currentPage.drawText(line, { x: toX, y: toY, font: helvetica, size: 9, color: DARK });
      toY -= 13;
    }
  }

  // Advance below whichever column is taller (each: label + 14, then 13 per line).
  const fromBottom = showFrom ? columnTopY - 14 - fromLines.length * 13 : columnTopY;
  const toBottom = toLines.length ? columnTopY - 14 - toLines.length * 13 : columnTopY;
  y = Math.min(fromBottom, toBottom) - 12;

  // Subject line (optional) — Helvetica-Bold, #1A1A1A
  if (subjectLine) {
    const subLines = wrapText(helveticaBold, subjectLine, 10, CONTENT_WIDTH);
    for (const line of subLines) {
      ensureSpace(15);
      y -= 15;
      currentPage.drawText(line, { x: LEFT_MARGIN, y, font: helveticaBold, size: 10, color: DARK });
    }
    y -= 6;
  }

  // Body — flowing paragraphs / { heading, text } blocks
  y -= 4;
  for (const block of bodyBlocks) {
    if (block && typeof block === 'object' && (block.heading || block.text)) {
      if (block.heading) {
        ensureSpace(BODY_LEADING);
        y -= BODY_LEADING;
        currentPage.drawText(block.heading, { x: LEFT_MARGIN, y, font: helveticaBold, size: 10, color: DARK });
      }
      if (block.text) drawParagraph(block.text, helvetica, BODY_SIZE, DARK);
    } else if (typeof block === 'string' && block.trim()) {
      drawParagraph(block, helvetica, BODY_SIZE, DARK);
    }
    y -= PARA_GAP;
  }

  // ACH / Wire block (optional) — same source fields + styling as the invoice
  if (includeAch) {
    ensureSpace(90);
    y -= 6;
    currentPage.drawLine({ start: { x: LEFT_MARGIN, y }, end: { x: RIGHT_MARGIN, y }, thickness: 0.5, color: LIGHT_LINE });
    y -= 14;
    currentPage.drawText(`Please make all checks payable to: ${s(settings.PaymentPayableTo)}`, {
      x: LEFT_MARGIN, y, font: helveticaBold, size: 9, color: DARK,
    });
    y -= 16;
    currentPage.drawText('ACH / Wire Instructions:', { x: LEFT_MARGIN, y, font: helveticaBold, size: 9, color: GREY });
    y -= 13;
    const payLines = [
      `Bank Name: ${s(settings.PaymentBankName)}`,
      `Bank Routing: ${s(settings.PaymentRouting)}`,
      `Account Number: ${s(settings.PaymentAccount)}`,
    ];
    for (const line of payLines) {
      currentPage.drawText(line, { x: LEFT_MARGIN + 10, y, font: helvetica, size: 9, color: DARK });
      y -= 12;
    }
    y -= 6;
  }

  // Signature block — only when the letter has body content. A blank or header-only
  // letter (clean stationery) gets no signature.
  const hasBody = Array.isArray(bodyBlocks) && bodyBlocks.some(
    (b) => (typeof b === 'string' ? b.trim() : b && (b.heading || b.text)),
  );
  if (hasBody) {
    const sigName = (signatureBlock && signatureBlock.name) || 'Marc Fragos';
    const sigTitle = (signatureBlock && signatureBlock.title) || '';
    const sigAgency = (signatureBlock && signatureBlock.agencyLine) || agencyName;

    // Keep-with-previous: only break to a new page if the WHOLE signature block plus
    // a small footer buffer genuinely won't fit. Sized to the block's real height
    // (Sincerely → wet-signature gap → name → [title] → agency), not a padded guess,
    // so the signature stays on the current page whenever the body leaves room for it
    // rather than orphaning onto a near-empty page 2.
    const sigBlockHeight = 10 + 34 + 13 + (sigTitle ? 12 : 0);
    const SIG_FOOTER_BUFFER = 12; // clearance kept above the footer rule
    ensureSpace(sigBlockHeight + SIG_FOOTER_BUFFER);
    y -= 10;
    currentPage.drawText('Sincerely,', { x: LEFT_MARGIN, y, font: helvetica, size: 10, color: DARK });
    y -= 34; // room for a wet signature
    currentPage.drawText(sigName, { x: LEFT_MARGIN, y, font: helveticaBold, size: 10, color: DARK });
    y -= 13;
    if (sigTitle) {
      currentPage.drawText(sigTitle, { x: LEFT_MARGIN, y, font: helvetica, size: 9, color: GREY });
      y -= 12;
    }
    currentPage.drawText(sigAgency, { x: LEFT_MARGIN, y, font: helvetica, size: 9, color: DARK });
  }

  // Footer on every page (drawn last so it can't be overwritten by body)
  for (const page of pages) drawFooter(page);

  return pdfDoc.save();
}

// ---- Helpers ----

function toRgb(c, fallback) {
  const v = c || fallback;
  return rgb(v.r, v.g, v.b);
}

function buildFooter(settings, s) {
  const addr = [
    s(settings.AgencyAddress),
    [s(settings.AgencyCity), s(settings.AgencyState), s(settings.AgencyZip)].filter(Boolean).join(', '),
  ].filter(Boolean).join(', ');
  return [s(settings.AgencyLegalName), addr, s(settings.AgencyPhone), s(settings.AgencyWebsite)]
    .filter(Boolean)
    .join('  •  ');
}

/**
 * Greedy width-based word wrap (the invoice has no reusable wrapper — it only
 * truncates via drawText maxWidth). Returns an array of lines that each fit
 * within maxWidth at the given font/size.
 */
function wrapText(font, text, size, maxWidth) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (cur && font.widthOfTextAtSize(test, size) > maxWidth) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}
